import React, { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Button, Tooltip } from 'antd'
import { Button as BsButton, OverlayTrigger, Tooltip as BsTooltip } from 'react-bootstrap'
import { InfoIcon } from '@primer/octicons-react'
import { useTranslation } from 'react-i18next'
import { IsoBox } from '../menus/IsoBox'
import { OptionImageUpload } from './OptionImageUpload'
import { NUMERIC_ERROR_DEFAULTS, numericFieldError } from './AddDisplayModeDialog'
import type { AddModeNumericField } from './AddDisplayModeDialog'
import { defaultOptionLabel, orientationDisplayName } from './OptionList'
import * as displayOptionService from '../services/display-option.service'
import type {
  CommitOptionImageResult,
  DisplayOptionDto,
  DisplayOptionWritePayload,
  Uom,
} from '../services/display-option.service'
import { deriveOrientationDims } from '../model/display-options'
import type { BaseDims, OrientationKey } from '../model/display-options'
import { seedOptionHangHoleCm } from '../model/hangable-defaults'
import { toCm } from '../model/units'

/**
 * fix-073 — the hole to SHOW for an option that carries none: the centre of
 * THIS option's own width, 0.5 cm down. Same rule as the product's own hang
 * point (`model/hangable-defaults.ts`, mirroring `addnew.js`), measured on
 * the orientation's permuted footprint rather than on the product's base
 * box, so a laydown is centred on ITS width and not on Front's.
 *
 * All-or-nothing, like everywhere else: an option that already carries a
 * hole — one axis of it included — is shown exactly as stored. `baseDims`
 * reaches this card in the PRODUCT's uom while the hole fields are read as
 * cm, so the footprint is converted before the rule is applied.
 */
const seededHoleForOption = (
  option: DisplayOptionDto,
  holes: { x: number | null; y: number | null },
  baseDims?: BaseDims & { uom?: string | null }
): { x: number | null; y: number | null } => {
  // An orientation IS a permutation of the product's box, so without the
  // base dims there is nothing to centre on. A display mode carries its own
  // dims and can be seeded in any host.
  if (!baseDims && option.type !== 'display_mode') return holes
  const uom = baseDims?.uom ?? option.uom ?? 'cm'
  const seeded = seedOptionHangHoleCm(
    displayOptionService.toCanonicalDisplayOption({
      ...option,
      hangHoleX: holes.x,
      hangHoleY: holes.y,
    }),
    baseDims
      ? {
          width: toCm(baseDims.width, uom),
          height: toCm(baseDims.height, uom),
          depth: toCm(baseDims.depth, uom),
        }
      : { width: 0, height: 0, depth: 0 }
  )
  return seeded ?? holes
}

/**
 * feat-004 story 2.4 (AC2, AC3, AC5) — the detail pane. Orientation: dims
 * read-only "(calculated)" text (never a disabled <input> — UX Form
 * Patterns), no name/capacity/archive/delete. Display mode: dims/name/
 * capacity editable, archive/delete available (lock+reason when blocked).
 * AC6: read-only mode renders every field as static text, never a disabled
 * control — the same "(calculated)" precedent used for orientation dims
 * applies to ALL fields when the card has no edit capability.
 */
/** Which OptionDetail call produced an `onChanged` — see the prop's note. */
export type OptionDetailAction =
  | 'save'
  | 'setDefault'
  | 'toggleEnabled'
  | 'archive'
  | 'delete'

export interface OptionDetailProps {
  option: DisplayOptionDto | null
  productId: number
  baseImageUrl: string
  /** Reported 2026-09-09: an orientation's width/height/depth are always
   *  null on its own DTO (only a display mode stores real dims) — the
   *  "(calculated)" text rendered a bare "—" for every orientation with no
   *  host wiring this in. Optional: a host that doesn't have the product's
   *  base dims handy (none currently omit it, but kept optional to match
   *  every other DisplayOptionsCard prop's back-compat convention) falls
   *  back to the old "—" behavior. */
  baseDims?: BaseDims & { uom?: string | null }
  readOnly: boolean
  service?: typeof displayOptionService
  /** Called after ANY successful mutation so the host can refetch the list. */
  /**
   * `action` (fix-035) says WHICH call produced `updated`. Only `'save'`
   * goes through `updateOption`, which is the one endpoint that also
   * mirrors a FRONT row onto the product record (fix-032) — the host needs
   * to tell that apart from `setDefault`/`enable`/`archive`, which touch
   * `isDefault`/`isEnabled`/`archivedAt` and never the product.
   */
  onChanged: (updated: DisplayOptionDto, action?: OptionDetailAction) => void
  /** AC5: surfaces a live-instance count from updateOption for AffectedInstancesConfirm. */
  onLiveInstanceCount: (count: number, optionName: string) => void
  onError: (message: string) => void
  /** story 2.6 AC5: gates `OptionImageUpload`'s upload control specifically
   * (`displayOption.image.upload`) — distinct from `readOnly`/`canEdit`. */
  canUploadImage: boolean
  /** story 2.6 AC2: forwarded to `OptionImageUpload`; forced up to
   * `DisplayOptionsCard` on a successful commit so it can merge the result
   * into `options` state (instant preview) and force a background refetch. */
  onImageUploaded: (uuid: string, result: CommitOptionImageResult) => void
}

/**
 * The UOM picker's options (reported 2026-10-08: "UOM like this, also remove
 * non from it").
 *
 * Sourced from the app's OWN `uomList` in `_services/common.service.js`
 * — `{ meters, cm, inches, feet }` with their full display names — which is
 * what every other Unit of Measure picker in Planigo offers, including the
 * Product Information tab beside this one. The hand-written list this
 * replaces was the backend `Uom` enum verbatim, so it additionally offered
 * `mm` and `none`: two values no other picker in the product exposes and
 * which no planning document ever specified (the PRD, the UX spec and the
 * source spec mention UOM only as a field name and never enumerate it).
 *
 * The leading blank matches the sibling picker's "-" placeholder row.
 */
/**
 * The app's Unit-of-Measure list, COPIED from `uomList` in
 * `_services/common.service.js` rather than imported.
 *
 * Not a preference — that module cannot be loaded here. It imports
 * `_translations/i18n.js` at module scope, which throws "You are passing an
 * undefined module! Please check the object you are passing to i18next.use()"
 * under Jest, taking down every suite that touches it (the same pre-existing
 * fault that already stops `SaveBlocking` and `SummaryView` from running at
 * all). Importing it here failed three card suites outright.
 *
 * Same accepted-duplication pattern this feature already uses for the
 * orientation labels in `export/shared/orientation-column.ts`, and with the
 * same obligation: keep in sync if the app's list ever changes.
 */
const APP_UOM_LIST: Record<string, string> = {
  meters: 'Meters',
  cm: 'Centimeters',
  inches: 'Inches',
  feet: 'Feet',
}

/** A native number input hands back a string; '' means "cleared". */
const toNumber = (raw: string): number | null => {
  if (raw.trim() === '') return null
  const parsed = Number(raw)
  return Number.isFinite(parsed) ? parsed : null
}

const UOM_OPTIONS: { value: string; label: string }[] = [
  { value: '', label: '-' },
  ...Object.entries(APP_UOM_LIST).map(([value, label]) => ({ value, label })),
]

export const OptionDetail: React.FC<OptionDetailProps> = ({
  option,
  productId,
  baseDims,
  readOnly,
  service = displayOptionService,
  onChanged,
  onLiveInstanceCount,
  onError,
  canUploadImage,
  onImageUploaded,
  // `baseImageUrl` stays on the props contract and is still passed by
  // DisplayOptionsCard, but nothing binds it: the dedicated-image field shows
  // the option's OWN image or an empty box, never the base product photo
  // (reported 2026-09-10). Binding it would trip no-unused-vars, which CRA
  // promotes to a build error under CI=true.
}) => {
  const { t } = useTranslation()
  const [form, setForm] = useState<DisplayOptionWritePayload>({})
  // Reported 2026-09-23: a single shared `saving` flag drove `loading` on
  // EVERY action button (Save/Set as default/Enable-Disable/Archive/Delete)
  // — clicking any one of them spun every other button too. Track which
  // specific action is in flight instead, so only the clicked button shows
  // its spinner.
  const [savingAction, setSavingAction] = useState<OptionDetailAction | null>(null)
  const isMounted = useRef(true)
  useEffect(
    () => () => {
      isMounted.current = false
    },
    []
  )

  /**
   * A dedicated image picked but not yet saved (user decision 2026-10-12: the
   * image commits on Save, like the Product Information tab's own image).
   *
   * The object URL is held in a ref ALONGSIDE the state value so cleanup can
   * revoke the previous one without re-running on every render — an unrevoked
   * blob URL is a leak that lives as long as the document. `imageEpoch` bumps
   * after a successful commit purely to remount `AcDropzone`, which keeps the
   * dropped file in its OWN internal state: without the remount it would go
   * on showing the staged file after the committed image has replaced it.
   */
  const [pendingImageFile, setPendingImageFile] = useState<File | null>(null)
  const [pendingPreviewUrl, setPendingPreviewUrl] = useState<string | null>(
    null
  )
  const [imageEpoch, setImageEpoch] = useState(0)
  const pendingPreviewUrlRef = useRef<string | null>(null)
  /**
   * Blob-URL helpers that never take the save down with them: the preview is
   * a convenience, the staged FILE is the thing that must survive to Save.
   * `URL.createObjectURL` is absent in jsdom and can throw in locked-down
   * embeddings — without the guard, picking a file would crash the pane.
   */
  const createPreviewUrl = (file: File): string | null => {
    try {
      return typeof URL !== 'undefined' &&
        typeof URL.createObjectURL === 'function'
        ? URL.createObjectURL(file)
        : null
    } catch {
      return null
    }
  }
  const revokePendingPreview = () => {
    if (pendingPreviewUrlRef.current) {
      try {
        if (typeof URL?.revokeObjectURL === 'function') {
          URL.revokeObjectURL(pendingPreviewUrlRef.current)
        }
      } catch {
        /* a preview URL that cannot be revoked is not worth a thrown render */
      }
      pendingPreviewUrlRef.current = null
    }
  }
  const clearPendingImage = () => {
    revokePendingPreview()
    setPendingImageFile(null)
    setPendingPreviewUrl(null)
  }
  const handleImageFileSelected = (file: File) => {
    revokePendingPreview()
    const url = createPreviewUrl(file)
    pendingPreviewUrlRef.current = url
    setPendingImageFile(file)
    setPendingPreviewUrl(url)
  }
  useEffect(() => () => revokePendingPreview(), [])

  useEffect(() => {
    if (!option) return
    setForm({
      name: option.name,
      width: option.width,
      height: option.height,
      depth: option.depth,
      uom: option.uom,
      capacity: option.capacity,
      isStackable: option.isStackable,
      maxStackableCount: option.maxStackableCount,
      isHangable: option.isHangable,
      // fix-073: a hangable option with no hole of its own shows the seeded
      // one (this orientation's centre), not a blank field that saves as
      // null. A non-hangable option keeps its nulls — the fields are hidden
      // anyway, and `effectiveFlags` reads a null hole as "cannot hang".
      ...(option.isHangable
        ? (() => {
            const hole = seededHoleForOption(
              option,
              { x: option.hangHoleX, y: option.hangHoleY },
              baseDims
            )
            return { hangHoleX: hole.x, hangHoleY: hole.y }
          })()
        : { hangHoleX: option.hangHoleX, hangHoleY: option.hangHoleY }),
    })
    // A save that's still in flight belongs to whichever option was selected
    // when it was triggered — switching rows must not leave the newly
    // selected option's buttons showing a stale/unrelated loading spinner.
    setSavingAction(null)
    // Same reasoning for a staged image: it was picked FOR the option that was
    // selected at the time. Carrying it over would attach one option's picture
    // to another on the next Save. `clearPendingImage` is deliberately NOT a
    // dependency — it is rebuilt every render and only ever touches a ref and
    // two setters, so depending on it would re-run this effect constantly. No
    // disable comment either: `react-hooks/exhaustive-deps` is not a
    // registered rule in this project's config, so suppressing it fails the
    // build outright (same note as `panels/DataPanel.tsx`'s own memo).
    clearPendingImage()
  }, [option])

  /**
   * The host's footer row, when there is one (reported 2026-10-08: the
   * actions should share Back's line, the way the other product tabs put
   * Update/Delete/GS1 Override there).
   *
   * Resolved from the DOM rather than during render because the slot is a
   * LATER sibling of the tab content in the host's tree — it does not exist
   * yet while this component first renders. Null in the two modal hosts,
   * which have no footer of their own; the footer then renders inline below
   * the card exactly as before, so nothing regresses there.
   *
   * Re-resolved after EVERY render, not once on mount (reported 2026-10-09:
   * "when i goes to the products options and comes back the buttons are not
   * showing"). The masterdata host renders the slot only while its Display
   * Options tab is active, but keeps this card MOUNTED while the user is on
   * another tab — `<Tab mountOnEnter>` with no `unmountOnExit`, deliberately,
   * so the card's in-memory state survives (addnew.js:1886). Leaving removes
   * the slot node and returning creates a NEW one, so a reference captured at
   * mount is detached by then and the portal renders into a node that is no
   * longer in the document: the buttons do not move, they vanish.
   *
   * `setFooterSlot` bails out when the node is unchanged, so running on every
   * render costs one `getElementById` and never loops. `useLayoutEffect`, not
   * `useEffect`, so the swap lands before paint and the inline fallback below
   * cannot flash.
   *
   * Declared ABOVE the `!option` early return, with every other hook
   * (reported 2026-10-12: "Rendered fewer hooks than expected" when deleting
   * a custom display mode). Deleting the selected mode is the one action that
   * makes `option` go null while this component stays mounted — the host's
   * post-delete refetch drops the row, so `options.find(uuid)` in
   * DisplayOptionsCard resolves to null on the very next render. With these
   * two hooks sitting below the return, that render called six hooks where
   * the previous one called eight, which is the error. Hooks must run
   * unconditionally; only the JSX below may branch.
   */
  const [footerSlot, setFooterSlot] = useState<HTMLElement | null>(null)
  useLayoutEffect(() => {
    const slot = document.getElementById('simv3-doc-footer-slot')
    setFooterSlot((prev) => (prev === slot ? prev : slot))
  })

  if (!option) {
    return (
      <div
        className="simv3-doc__detail simv3-doc__section"
        data-testid="option-detail-empty"
      >
        {t('simulationV3.displayOptions.detail.empty', {
          defaultValue: 'Select an option to view details',
        })}
      </div>
    )
  }

  const isOrientation = option.type === 'orientation'
  const label =
    isOrientation && option.orientationKey != null
      ? orientationDisplayName(t, option.orientationKey)
      : option.name || ''

  // An orientation's own width/height/depth are always null (only a display
  // mode stores real dims) — derive the actual value from the product's base
  // dims + the same 6-permutation table the backend/canvas already use,
  // instead of showing a bare "—" for every orientation.
  const derivedDims =
    isOrientation &&
    baseDims &&
    option.orientationKey != null &&
    option.orientationKey >= 1 &&
    option.orientationKey <= 6
      ? deriveOrientationDims(baseDims, option.orientationKey as OrientationKey)
      : null
  const calculatedUom = baseDims?.uom ?? option.uom ?? ''
  /**
   * What the isometric preview is shaped by (PLAN-TC-1377: "Isometric box
   * proportions correspond to Side dimensions", and for a display mode
   * "Preview updates to display-mode proportions").
   *
   * Orientations take `derivedDims` (the permutation of the product's base
   * dims, computed just above — an orientation row stores no dims of its
   * own); display modes take the dims they actually store. Either can be
   * absent, and then the box stays the cube it has always been.
   */
  const isoDims = isOrientation
    ? derivedDims
    : option.width != null && option.height != null && option.depth != null
      ? { width: option.width, height: option.height, depth: option.depth }
      : null

  const runAction = async (
    actionName: NonNullable<typeof savingAction>,
    action: () => Promise<DisplayOptionDto>
  ) => {
    setSavingAction(actionName)
    try {
      const updated = await action()
      if (isMounted.current) onChanged(updated, actionName)
    } catch (err) {
      if (isMounted.current)
        onError(err instanceof Error ? err.message : String(err))
    } finally {
      if (isMounted.current) setSavingAction(null)
    }
  }

  /**
   * fix-019 (reported 2026-10-16): the edit form saved 0 in Width/Height/
   * Depth/Capacity — the same hole the create dialog had. Same rule as
   * there, display modes only: an orientation's dims are calculated and its
   * capacity is immutable. Revised 2026-10-19: blank and 0 are allowed,
   * only a negative (or a fractional capacity) is rejected.
   */
  const numericErrors: Partial<
    Record<AddModeNumericField, ReturnType<typeof numericFieldError>>
  > = {}
  const numericFields: AddModeNumericField[] = ['width', 'height', 'depth', 'capacity']
  if (!isOrientation) {
    numericFields.forEach((field) => {
      const error = numericFieldError(field, form[field] ?? null)
      if (error) numericErrors[field] = error
    })
  }
  const numbersValid = Object.keys(numericErrors).length === 0

  const renderNumericError = (field: AddModeNumericField) => {
    const error = numericErrors[field]
    if (!error || readOnly) return null
    return (
      <div
        className="simv3-doc__field-error"
        style={{ color: '#dc3545', fontSize: 12, marginTop: 2 }}
        data-testid={`option-detail-${field}-error`}
      >
        {t(`simulationV3.displayOptions.dialog.addMode.errors.${error}`, {
          defaultValue: NUMERIC_ERROR_DEFAULTS[error],
        })}
      </div>
    )
  }

  const handleSave = () => {
    if (!numbersValid) return
    return saveOption()
  }

  const saveOption = () =>
    runAction('save', async () => {
      // The image goes FIRST and, on failure, aborts the whole save: a field
      // write that lands while the picture silently does not would leave the
      // user looking at a "saved" card whose image is still the old one. The
      // form edits stay in local state, so a retry loses nothing.
      if (pendingImageFile) {
        const imageResult = await service.uploadAndCommitOptionImage(
          productId,
          option.uuid,
          pendingImageFile
        )
        if (isMounted.current) {
          clearPendingImage()
          setImageEpoch((epoch) => epoch + 1)
          // Hand the resolved image up before the field save returns, so the
          // card's own state shows the new picture even if `updateOption`
          // below fails.
          onImageUploaded(option.uuid, imageResult)
        }
      }
      const result = await service.updateOption(productId, option.uuid, form)
      // AC5: post-save disclosure (see AffectedInstancesConfirm — this is
      // NOT a pre-save gate, the write already happened by this point).
      // Use the SAVED result's own name (not the pre-edit `label` closure
      // variable) so a same-save rename doesn't show the stale name here.
      if (result.liveInstanceCount != null && result.liveInstanceCount > 0) {
        const savedLabel =
          result.option.type === 'orientation' &&
          result.option.orientationKey != null
            ? orientationDisplayName(t, result.option.orientationKey)
            : result.option.name || ''
        onLiveInstanceCount(result.liveInstanceCount, savedLabel)
      }
      return result.option
    })

  const handleSetDefault = () =>
    runAction('setDefault', async () => {
      const result = await service.setDefaultOption(productId, option.uuid)
      return result.newDefault
    })

  const handleToggleEnabled = () =>
    runAction('toggleEnabled', () =>
      option.isEnabled
        ? service.disableOption(productId, option.uuid)
        : service.enableOption(productId, option.uuid)
    )

  const handleArchive = () =>
    runAction('archive', () => service.archiveOption(productId, option.uuid))

  const handleDelete = () =>
    runAction('delete', async () => {
      await service.deleteOption(productId, option.uuid)
      // deleteOption has no "keep and re-select" result (the row is gone) —
      // hand back the just-deleted option so onChanged's refetch runs; the
      // host's refetch/select-fallback logic already handles a vanished uuid.
      return option
    })

  // Lock+reason (AC3) — mirrors the backend's own blocking rules exactly so
  // the UI never offers an action the server will reject. `archivedAt` gates
  // enable/disable too: the backend's own archive() forces isEnabled=false
  // with no un-archive endpoint, so re-enabling an archived option from here
  // would leave it enabled-but-archived, an inconsistent state.
  const disableLockReason = option.isDefault
    ? t('simulationV3.displayOptions.detail.lockDefaultDisable', {
        defaultValue: 'Cannot disable the default option',
      })
    : option.archivedAt
      ? t('simulationV3.displayOptions.detail.lockArchivedToggle', {
          defaultValue: 'Cannot change enabled state of an archived option',
        })
      : null
  const archiveLockReason = isOrientation
    ? t('simulationV3.displayOptions.detail.lockOrientationArchive', {
        defaultValue: 'Orientations cannot be archived — use disable',
      })
    : option.isDefault
      ? t('simulationV3.displayOptions.detail.lockDefaultArchive', {
          defaultValue: 'Cannot archive the default option',
        })
      : null
  const setDefaultLockReason =
    option.isDefault || !option.isEnabled || option.archivedAt
      ? t('simulationV3.displayOptions.detail.lockSetDefault', {
          defaultValue: 'Cannot set a disabled or archived option as default',
        })
      : null

  /**
   * An action that cannot currently be used stays VISIBLE as a DISABLED
   * button carrying its reason, rather than collapsing to a bare padlock
   * (reported 2026-10-08: "why this shows 2 locks … if theres a button show
   * disabled buttons").
   *
   * A lone glyph gives no clue WHICH action it stands for, and the default
   * display mode hit the worst case: both "Disable" and "Archive" lock for a
   * default option, so the row rendered two identical padlocks side by side
   * with nothing to tell them apart. A disabled button names itself.
   *
   * The reason moves from the padlock's tooltip onto the button's. A disabled
   * AntD button swallows pointer events, so the tooltip hangs off a wrapper
   * span — which also keeps the `lock-*` testid the suite uses to mean "this
   * action is locked".
   *
   * (This is the opposite stance to `renderField`'s below, deliberately: UX
   * Form Patterns rejects disabled INPUTS because they read as "unlock me",
   * which is exactly the right signal for an action that genuinely is
   * blocked and has a reason to show.)
   */
  const lockableAction = (
    lockTestId: string,
    lockReason: string | null,
    button: React.ReactNode
  ) =>
    lockReason ? (
      /* AntD caps a tooltip at its `tooltipMaxWidth` token — 250px, emitted by
         cssinjs onto the overlay root (`width: max-content; max-width: 250px`).
         Every reason here is one short sentence that sits just over that cap in
         Hebrew, so the box wrapped its LAST word onto a second line and split a
         two-word phrase down the middle — "לא ניתן להשבית את אפשרות ברירת /
         המחדל" (QA-reported 2026-10-21). `styles.root` lands as a real inline
         style on that same element, so it wins over the generated rule without
         a specificity fight and without a global token override that would
         widen every tooltip in the app.

         420px, not `nowrap`: the longest reason here ("Cannot set a disabled
         or archived option as default" / "לא ניתן לקבוע אפשרות מושבתת או
         בארכיון כברירת מחדל") measures ~355px at this font, so all of them
         now fit on one line, while anything longer added later still wraps
         rather than running off the viewport. */
      <Tooltip title={lockReason} styles={{ root: { maxWidth: 420 } }}>
        <span className="simv3-doc__btn-locked" data-testid={lockTestId}>
          {button}
        </span>
      </Tooltip>
    ) : (
      button
    )

  // AC6: when the card has no edit capability, EVERY field renders as
  // static text — never a disabled <input>/<Select>/<Switch> (UX Form
  // Patterns: disabled inputs "suggest unlock me").
  const renderField = (value: React.ReactNode, control: React.ReactNode) =>
    readOnly ? (
      <span className="simv3-doc__readonly-value">{value}</span>
    ) : (
      control
    )

  /**
   * One dimension field. Pulled out of the old `.map` because the three now
   * land in different grid cells rather than flowing through one container.
   */
  const dimField = (dim: 'width' | 'height' | 'depth') => (
    <div className="simv3-doc__field">
      <label className="simv3-doc__field-label">
        {t(`simulationV3.displayOptions.detail.${dim}`, { defaultValue: dim })}
      </label>
      {isOrientation ? (
        <span
          className="simv3-doc__calculated"
          data-testid={`option-detail-${dim}-calculated`}
        >
          {(derivedDims ? derivedDims[dim] : option[dim]) ?? '—'}{' '}
          {calculatedUom} (
          {t('simulationV3.displayOptions.detail.calculated', {
            defaultValue: 'calculated',
          })}
          )
        </span>
      ) : (
        renderField(
          form[dim] ?? '—',
          <input
            type="number"
            min={0}
            className="form-control form-control-sm form__field"
            value={form[dim] ?? ''}
            onChange={(e) => {
              // Read before queueing: a queued updater sees the controlled
              // input already restored, so a second edit was lost (fix-019).
              const next = toNumber(e.target.value)
              setForm((f) => ({ ...f, [dim]: next }))
            }}
            data-testid={`option-detail-${dim}-input`}
          />
        )
      )}
      {!isOrientation && renderNumericError(dim)}
    </div>
  )

  /**
   * Footer order requested 2026-10-09: Set as default | Delete | Disable |
   * Archive, then Save last. Flagged at the time and kept as asked — Delete
   * sitting between two benign actions is a misclick risk, and Save trailing
   * the lifecycle group reads unusually for a primary commit. Change the
   * order here if that is ever revisited.
   */
  const footerNode = (
    <div className="simv3-doc__detail-footer">
      <div className="simv3-doc__field simv3-doc__actions">
        {!option.isDefault && (!readOnly || setDefaultLockReason)
          ? lockableAction(
              'lock-set-default',
              setDefaultLockReason,
              <Button
                loading={savingAction === 'setDefault'}
                disabled={
                  !!setDefaultLockReason ||
                  (savingAction != null && savingAction !== 'setDefault')
                }
                onClick={setDefaultLockReason ? undefined : handleSetDefault}
                className="simv3-doc__btn simv3-doc__btn--ghost"
                data-testid="option-detail-set-default"
              >
                {t('simulationV3.displayOptions.detail.setDefault', {
                  defaultValue: 'Set as default',
                })}
              </Button>
            )
          : null}

        {/* Delete (display modes only, AC3/story 2.1's deleteOption) — there
            is no separate "preview" endpoint (same situation as
            AffectedInstancesConfirm, see Dev Notes), so whether a live-
            placement count would block the delete is only known once the
            backend rejects it; that rejection surfaces via onError. */}
        {!isOrientation && !readOnly ? (
          <Button
            loading={savingAction === 'delete'}
            disabled={savingAction != null && savingAction !== 'delete'}
            onClick={handleDelete}
            className="simv3-doc__btn simv3-doc__btn--delete"
            data-testid="option-detail-delete"
          >
            {t('simulationV3.displayOptions.detail.delete', {
              defaultValue: 'Delete',
            })}
          </Button>
        ) : null}

        {!readOnly || disableLockReason
          ? lockableAction(
              'lock-toggle-enabled',
              disableLockReason,
              <Button
                loading={savingAction === 'toggleEnabled'}
                disabled={
                  !!disableLockReason ||
                  (savingAction != null && savingAction !== 'toggleEnabled')
                }
                onClick={disableLockReason ? undefined : handleToggleEnabled}
                className="simv3-doc__btn simv3-doc__btn--info"
                data-testid="option-detail-toggle-enabled"
              >
                {option.isEnabled
                  ? t('simulationV3.displayOptions.detail.disable', {
                      defaultValue: 'Disable',
                    })
                  : t('simulationV3.displayOptions.detail.enable', {
                      defaultValue: 'Enable',
                    })}
              </Button>
            )
          : null}

        {!isOrientation &&
        option.archivedAt == null &&
        (!readOnly || archiveLockReason)
          ? lockableAction(
              'lock-archive',
              archiveLockReason,
              <Button
                loading={savingAction === 'archive'}
                disabled={
                  !!archiveLockReason ||
                  (savingAction != null && savingAction !== 'archive')
                }
                onClick={archiveLockReason ? undefined : handleArchive}
                className="simv3-doc__btn simv3-doc__btn--archive"
                data-testid="option-detail-archive"
              >
                {t('simulationV3.displayOptions.detail.archive', {
                  defaultValue: 'Archive',
                })}
              </Button>
            )
          : null}
      </div>

      {!readOnly && (
        <Button
          className="simv3-doc__btn simv3-doc__btn--primary"
          loading={savingAction === 'save'}
          disabled={savingAction != null && savingAction !== 'save'}
          onClick={handleSave}
          data-testid="option-detail-save"
        >
          {t('simulationV3.displayOptions.detail.save', {
            defaultValue: 'Save',
          })}
        </Button>
      )}
    </div>
  )

  return (
    <div
      className={`simv3-doc__detail${readOnly ? ' simv3-doc--readonly' : ''}`}
      data-testid="option-detail"
    >
      {/* One white card per group, each with its own <h4> — the Product
          Information tab's own arrangement (reported 2026-10-09: "hangable
          has separate card … both needs separate cards same as the products
          options"). `addnew.js:1546-1600` builds that tab from `.form-section`
          cards and lays every two-up pair out as
          `<Col xs=5> … <Col xs=2/> … <Col xs=5>`; the same Bootstrap grid is
          used literally here rather than re-derived, which is what made the
          field widths differ before.

          `.simv3-doc__detail-body` survives as the group around the cards
          because it is what marks "fields here, buttons not" for the footer
          test — it is no longer a panel of its own. */}
      <div className="simv3-doc__detail-body">
        <div className="simv3-doc__section">
          {/* Reported 2026-09-10: keep the isometric box outline as a visual
            anchor for the selected row, but drop the photo inside it — this
            hero preview duplicated OptionImageUpload's own preview further
            down (which is the one that actually resolves a dedicated image;
            this box always showed the base product photo regardless). */}
          {/* Proportioned from the SELECTED option's own effective dims
            (PLAN-TC-1376/1377, source ticket T-10) — `derivedDims` for an
            orientation, the mode's own stored dims for a display mode. Null
            for either (no base dims passed by the host, or a mode with no
            dims saved yet) falls the box back to the plain cube. */}
          <IsoBox
            imageUrl=""
            orientationKey={option.orientationKey}
            isDedicated={false}
            size="M"
            label={label}
            dims={isoDims}
          />
          {/* Reported 2026-10-13: the title says WHICH option is open but not
              that it is the default one — the star lives only on the list row,
              which is easy to lose track of once the detail pane is scrolled.
              Same wording and same italic metadata voice as the row marker,
              parenthesised because here it trails a title inline. */}
          <h3>
            {label}
            {option.isDefault ? (
              <span className="simv3-doc__detail-default">
                ({defaultOptionLabel(t)})
              </span>
            ) : null}
          </h3>

          {!isOrientation && (
            <div className="row">
              <div className="col-12">
                <div className="simv3-doc__field">
                  <label className="simv3-doc__field-label">
                    {t('simulationV3.displayOptions.detail.name', {
                      defaultValue: 'Name',
                    })}
                  </label>
                  {renderField(
                    form.name || '',
                    <input
                      type="text"
                      className="form-control form-control-sm form__field"
                      value={form.name ?? ''}
                      onChange={(e) =>
                        setForm((f) => ({ ...f, name: e.target.value }))
                      }
                      data-testid="option-detail-name"
                    />
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* The host's `scales` card, field for field: Width | Height, then
          Depth | Unit of Measure, then one half-width row underneath — where
          Product Information puts Sensitivity and this card puts Capacity. */}
        <div className="simv3-doc__section">
          <h4>{t('scales', { defaultValue: 'Dimensions' })}</h4>

          <div className="simv3-doc__dims">
            <div className="row">
              <div className="col-5">{dimField('width')}</div>
              <div className="col-2" />
              <div className="col-5">{dimField('height')}</div>
            </div>

            <div className="row">
              <div className="col-5">{dimField('depth')}</div>
              <div className="col-2" />
              <div className="col-5">
                {!isOrientation && (
                  <div className="simv3-doc__field">
                    <label className="simv3-doc__field-label">
                      {/* The host's own key, so this reads "Unit of Measure"
                        beside its Depth exactly as the Product Information
                        tab does - its own label was the shorter "UOM". */}
                      {t('uomeasure', { defaultValue: 'Unit of Measure' })}
                    </label>
                    {renderField(
                      form.uom ?? '—',
                      <select
                        className="form-control form-control-sm form__field simv3-doc__uom"
                        value={form.uom ?? ''}
                        onChange={(e) =>
                          setForm((f) => ({
                            ...f,
                            uom: (e.target.value || null) as Uom,
                          }))
                        }
                        data-testid="option-detail-uom"
                      >
                        {UOM_OPTIONS.map((u) => (
                          <option key={u.value} value={u.value}>
                            {u.label}
                          </option>
                        ))}
                      </select>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Capacity is a display-mode-only field (story 2.1's
            rejectImmutableOrientationEdits rejects a changed capacity for
            orientations, same immutability class as name/dims/orientationKey
            — AC2's parenthetical clarifies this over its own opening
            clause). */}
          {!isOrientation && (
            <div className="row">
              <div className="col-5">
                <div className="simv3-doc__field">
                  <label className="simv3-doc__field-label">
                    {t('simulationV3.displayOptions.detail.capacity', {
                      defaultValue: 'Capacity (units per pack)',
                    })}
                  </label>
                  {renderField(
                    form.capacity ?? '—',
                    <input
                      type="number"
                      min={0}
                      className="form-control form-control-sm form__field"
                      value={form.capacity ?? ''}
                      onChange={(e) => {
                        const next = toNumber(e.target.value)
                        setForm((f) => ({ ...f, capacity: next }))
                      }}
                      data-testid="option-detail-capacity"
                    />
                  )}
                  {renderNumericError('capacity')}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Dedicated image — its own card, in the Product Information tab's
          own idiom (that tab's `img` key for the heading, its `AcDropzone`,
          its thumbnail), sitting between Dimensions and Stackable where it
          was asked for. Offered for EVERY option type: an orientation needs a
          real photograph at least as much as a display mode does, since
          orientations 3-6 otherwise fall back to the base photo stretched and
          dimmed (FR-27). The pre-2026-09-10 widget was gated to display modes
          only; that gate is deliberately gone. */}
        <div className="simv3-doc__section">
          <h4>{t('img', { defaultValue: 'Image' })}</h4>
          <div className="row">
            <div className="col-12">
              <OptionImageUpload
                key={`${option.uuid}:${imageEpoch}`}
                imageUrl={option.imageUrl}
                hasDedicatedImage={!!option.hasDedicatedImage}
                pendingPreviewUrl={pendingPreviewUrl}
                hasPendingImage={pendingImageFile != null}
                canUpload={canUploadImage && !readOnly}
                disabled={savingAction === 'save'}
                onFileSelected={handleImageFileSelected}
                // fix-017: the Master Data zoom viewer enlarges to the full-size
                // image, as its product image does (AcDropzone skips it in simV3)
                resolveFullImageUrl={
                  service.getOptionBiggerImageUrl
                    ? () => service.getOptionBiggerImageUrl(productId, option.uuid)
                    : undefined
                }
              />
            </div>
          </div>
        </div>

        {/* Stackable and Hangable each get their own card with their own
          heading, the way Product Information gives Hangable one — headings
          and toggle labels use the host's OWN translation keys (`STACKABLE`,
          `HANGABLE`, `IS_HANGABLE`), so the wording matches it in Hebrew as
          well as English. `IS_STACKABLE` was the one key the app did not
          already have; it was added alongside the others. */}
        <div className="simv3-doc__section">
          <h4>{t('STACKABLE', { defaultValue: 'Stackable' })}</h4>

          {readOnly ? (
            <div className="simv3-doc__field">
              <label className="simv3-doc__field-label">
                {t('IS_STACKABLE', { defaultValue: 'Is Stackable' })}
              </label>
              <span className="simv3-doc__readonly-value">
                {form.isStackable
                  ? t('simulationV3.displayOptions.detail.yes', {
                      defaultValue: 'Yes',
                    })
                  : t('simulationV3.displayOptions.detail.no', {
                      defaultValue: 'No',
                    })}
              </span>
            </div>
          ) : (
            <div>
              <label className="pure-material-switch" style={{ width: '100%' }}>
                <input
                  type="checkbox"
                  checked={!!form.isStackable}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, isStackable: e.target.checked }))
                  }
                  data-testid="option-detail-stackable"
                />
                <span>
                  {t('IS_STACKABLE', { defaultValue: 'Is Stackable' })}
                </span>
              </label>
            </div>
          )}

          {/* Reported 2026-09-10: this stayed visible and fully interactive
            even with Stackable off, showing a stored value that has no
            effect until stacking is actually enabled — hidden entirely
            instead, matching how the rest of this card only shows fields
            that currently mean something (e.g. `!isOrientation` above). */}
          {form.isStackable && (
            <div className="row">
              <div className="col-5">
                <div className="simv3-doc__field">
                  <label className="simv3-doc__field-label">
                    {t('simulationV3.displayOptions.detail.maxStackableCount', {
                      defaultValue: 'Max stack count',
                    })}
                    <OverlayTrigger
                      overlay={
                        <BsTooltip>
                          {t(
                            'simulationV3.displayOptions.detail.maxStackableCountHint',
                            {
                              defaultValue:
                                "Leave this field blank or 0 if you don't want to limit stackable count",
                            }
                          )}
                        </BsTooltip>
                      }
                    >
                      <BsButton
                        variant="secondary"
                        className="stackwarn-warn"
                        data-testid="option-detail-max-stackable-count-hint"
                      >
                        <InfoIcon size={15} />
                      </BsButton>
                    </OverlayTrigger>
                  </label>
                  {renderField(
                    form.maxStackableCount ?? '—',
                    <input
                      type="number"
                      min={0}
                      className="form-control form-control-sm form__field"
                      value={form.maxStackableCount ?? ''}
                      onChange={(e) =>
                        setForm((f) => ({
                          ...f,
                          maxStackableCount: toNumber(e.target.value) ?? 0,
                        }))
                      }
                      data-testid="option-detail-max-stackable-count"
                    />
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="simv3-doc__section">
          <h4>{t('HANGABLE', { defaultValue: 'Hangable' })}</h4>

          {readOnly ? (
            <div className="simv3-doc__field">
              <label className="simv3-doc__field-label">
                {t('IS_HANGABLE', { defaultValue: 'Is Hangable' })}
              </label>
              <span className="simv3-doc__readonly-value">
                {form.isHangable
                  ? t('simulationV3.displayOptions.detail.yes', {
                      defaultValue: 'Yes',
                    })
                  : t('simulationV3.displayOptions.detail.no', {
                      defaultValue: 'No',
                    })}
              </span>
            </div>
          ) : (
            <div>
              <label className="pure-material-switch" style={{ width: '100%' }}>
                <input
                  type="checkbox"
                  checked={!!form.isHangable}
                  onChange={(e) =>
                    setForm((f) => {
                      if (!e.target.checked) return { ...f, isHangable: false }
                      // fix-073: turning hanging ON reveals the two fields,
                      // so they are seeded now — otherwise the planner is
                      // shown an empty hole for an option that has one by
                      // construction (its own centre).
                      const hole = seededHoleForOption(
                        option,
                        { x: f.hangHoleX ?? null, y: f.hangHoleY ?? null },
                        baseDims
                      )
                      return {
                        ...f,
                        isHangable: true,
                        hangHoleX: hole.x,
                        hangHoleY: hole.y,
                      }
                    })
                  }
                  data-testid="option-detail-hangable"
                />
                <span>{t('IS_HANGABLE', { defaultValue: 'Is Hangable' })}</span>
              </label>
            </div>
          )}

          {/* Reported 2026-09-10: same reasoning as Max stack count — a hole
            coordinate has no effect until Hangable is on, so both fields
            are hidden entirely rather than shown disabled. */}
          {form.isHangable && (
            <div className="row">
              <div className="col-5">
                <div className="simv3-doc__field">
                  <label className="simv3-doc__field-label">
                    {t('simulationV3.displayOptions.detail.hangHoleX', {
                      defaultValue: 'Horizontal',
                    })}
                  </label>
                  {renderField(
                    form.hangHoleX ?? '—',
                    <input
                      type="number"
                      className="form-control form-control-sm form__field"
                      value={form.hangHoleX ?? ''}
                      onChange={(e) =>
                        setForm((f) => ({
                          ...f,
                          hangHoleX: toNumber(e.target.value) ?? null,
                        }))
                      }
                      data-testid="option-detail-hang-hole-x"
                    />
                  )}
                </div>
              </div>
              <div className="col-2" />
              <div className="col-5">
                <div className="simv3-doc__field">
                  <label className="simv3-doc__field-label">
                    {t('simulationV3.displayOptions.detail.hangHoleY', {
                      defaultValue: 'Vertical',
                    })}
                  </label>
                  {renderField(
                    form.hangHoleY ?? '—',
                    <input
                      type="number"
                      className="form-control form-control-sm form__field"
                      value={form.hangHoleY ?? ''}
                      onChange={(e) =>
                        setForm((f) => ({
                          ...f,
                          hangHoleY: toNumber(e.target.value) ?? null,
                        }))
                      }
                      data-testid="option-detail-hang-hole-y"
                    />
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Rendered into the host's footer row when it offers one, so the
          actions sit on Back's line like the other product tabs' do;
          inline below the card otherwise (the modal hosts).

          `isConnected` guards the one render between the host detaching a
          slot and the layout effect above finding its replacement: without
          it that render would portal into a detached node and show nothing.
          Falling back to inline keeps the actions reachable whatever the
          host does with the node. */}
      {footerSlot && footerSlot.isConnected
        ? createPortal(footerNode, footerSlot)
        : footerNode}

      {/* 2026-09-10 the dedicated-image widget was pulled from this pane
          ("image upload is out of scope"), with its plumbing left in place in
          case the call was revisited. It was, on 2026-10-12 — the field is
          back above, between Dimensions and Stackable, now saving on Save
          rather than on drop. */}
    </div>
  )
}
